import { Component, forwardRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  FormControl,
  Validators,
} from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';
import { Subscription } from 'rxjs';

export interface RecurrenceConfig {
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  endType: 'NEVER' | 'AFTER_COUNT' | 'UNTIL_DATE';
  endCount?: number;
  endDate?: string;
}

@Component({
  selector: 'app-recurrence-config',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './recurrence-config.component.html',
  styleUrl: './recurrence-config.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RecurrenceConfigComponent),
      multi: true,
    },
  ],
})
export class RecurrenceConfigComponent implements ControlValueAccessor, OnDestroy {
  enabled = false;
  selectedDays: number[] = [];

  frequencyControl = new FormControl('WEEKLY');
  intervalControl = new FormControl(1, [Validators.required, Validators.min(1)]);
  dayOfMonthControl = new FormControl(1, [Validators.required, Validators.min(1)]);
  endTypeControl = new FormControl('NEVER');
  endCountControl = new FormControl(10, [Validators.required, Validators.min(1)]);
  endDateControl = new FormControl(null);

  weekDays = [
    { value: 0, label: 'Su' },
    { value: 1, label: 'Mo' },
    { value: 2, label: 'Tu' },
    { value: 3, label: 'We' },
    { value: 4, label: 'Th' },
    { value: 5, label: 'Fr' },
    { value: 6, label: 'Sa' },
  ];

  private onChange: (value: RecurrenceConfig | null) => void = () => {};
  private onTouched: () => void = () => {};
  private subscriptions = new Subscription();

  constructor() {
    this.subscriptions.add(
      this.frequencyControl.valueChanges.subscribe(() => this.emitValue())
    );
    this.subscriptions.add(
      this.intervalControl.valueChanges.subscribe(() => this.emitValue())
    );
    this.subscriptions.add(
      this.dayOfMonthControl.valueChanges.subscribe(() => this.emitValue())
    );
    this.subscriptions.add(
      this.endTypeControl.valueChanges.subscribe(() => this.emitValue())
    );
    this.subscriptions.add(
      this.endCountControl.valueChanges.subscribe(() => this.emitValue())
    );
    this.subscriptions.add(
      this.endDateControl.valueChanges.subscribe(() => this.emitValue())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  writeValue(value: RecurrenceConfig | null): void {
    if (value) {
      this.enabled = true;
      const isWeekdays = value.frequency === 'WEEKLY' &&
        value.daysOfWeek?.length === 5 &&
        [1, 2, 3, 4, 5].every(d => value.daysOfWeek.includes(d));
      this.frequencyControl.setValue(isWeekdays ? 'WEEKDAYS' : value.frequency, { emitEvent: false });
      this.intervalControl.setValue(value.interval, { emitEvent: false });
      this.selectedDays = value.daysOfWeek || [];
      this.dayOfMonthControl.setValue(value.dayOfMonth || 1, { emitEvent: false });
      this.endTypeControl.setValue(value.endType, { emitEvent: false });
      this.endCountControl.setValue(value.endCount || 10, { emitEvent: false });
      this.endDateControl.setValue(value.endDate ? new Date(value.endDate) : null, { emitEvent: false });
    } else {
      this.enabled = false;
    }
  }

  registerOnChange(fn: (value: RecurrenceConfig | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onFrequencyChange(value: string): void {
    if (value === 'WEEKDAYS' || value === 'WEEKLY') {
      this.selectedDays = [1, 2, 3, 4, 5]; // Mon-Fri default
      this.intervalControl.setValue(1, { emitEvent: false });
    }
    this.emitValue();
  }

  enforceMin(control: FormControl, min: number): void {
    if (!control.value || control.value < min) {
      control.setValue(min);
    }
  }

  preventNegative(event: KeyboardEvent): void {
    if (event.key === '-' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  onToggle(checked: boolean): void {
    this.enabled = checked;
    if (checked && this.selectedDays.length === 0) {
      this.selectedDays = [1, 2, 3, 4, 5]; // Mon-Fri default
    }
    this.emitValue();
    this.onTouched();
  }

  isDaySelected(day: number): boolean {
    return this.selectedDays.includes(day);
  }

  toggleDay(day: number): void {
    const index = this.selectedDays.indexOf(day);
    if (index >= 0) {
      if (this.selectedDays.length <= 1) return; // keep at least one day selected
      this.selectedDays = this.selectedDays.filter((d) => d !== day);
    } else {
      this.selectedDays = [...this.selectedDays, day];
    }
    this.emitValue();
  }

  private emitValue(): void {
    if (!this.enabled) {
      this.onChange(null);
      return;
    }

    const isWeekdays = this.frequencyControl.value === 'WEEKDAYS';
    const value: RecurrenceConfig = {
      frequency: isWeekdays ? 'WEEKLY' : this.frequencyControl.value as any,
      interval: this.intervalControl.value || 1,
      endType: this.endTypeControl.value as any,
    };

    if (isWeekdays) {
      value.daysOfWeek = [1, 2, 3, 4, 5];
    } else if (this.frequencyControl.value === 'WEEKLY' && this.selectedDays.length > 0) {
      value.daysOfWeek = this.selectedDays;
    }

    if (this.frequencyControl.value === 'MONTHLY') {
      value.dayOfMonth = this.dayOfMonthControl.value || 1;
    }

    if (this.endTypeControl.value === 'AFTER_COUNT') {
      value.endCount = this.endCountControl.value || 10;
    }

    if (this.endTypeControl.value === 'UNTIL_DATE' && this.endDateControl.value) {
      value.endDate = new Date(this.endDateControl.value).toISOString();
    }

    this.onChange(value);
  }
}
